import { useState, useEffect } from "react";
import { 
  Award, TrendingUp, Gift, History, 
  ArrowUpCircle, ArrowDownCircle, ShoppingBag, Star, Check, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PointsPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redeeming, setRedeeming] = useState(null);
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);

  useEffect(() => {
    fetchPointsSummary();
    fetchRewards();
  }, []);

  const fetchPointsSummary = async () => {
    try {
      const res = await authFetch(`${API}/points/summary`, { });
      if (res.ok) {
        setSummary(await res.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento punti");
    } finally {
      setLoading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      const res = await authFetch(`${API}/points/rewards`, { });
      if (res.ok) {
        setRewards(await res.json());
      }
    } catch (error) {
      console.error("Error fetching rewards:", error);
    }
  };

  const handleRedeemClick = (reward) => {
    setSelectedReward(reward);
    setShowRedeemDialog(true);
  };

  const handleConfirmRedeem = async () => {
    if (!selectedReward) return;
    
    setRedeeming(selectedReward.reward_id);
    try {
      const res = await authFetch(`${API}/points/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reward_id: selectedReward.reward_id })
      });
      
      if (res.ok) {
        toast.success(`Premio "${selectedReward.name}" riscattato!`);
        fetchPointsSummary();
        setShowRedeemDialog(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel riscatto");
      }
    } catch (error) {
      toast.error("Errore nel riscatto");
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="points-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Award className="w-8 h-8 text-primary" />
          I Miei Punti
        </h1>
        <p className="text-zinc-400 mt-1">
          Accumula punti con ogni ordine e riscattali per premi esclusivi
        </p>
      </div>

      {/* Points Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-6 bg-gradient-to-br from-primary/20 to-transparent border-primary/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Punti Disponibili</p>
              <p className="text-4xl font-bold text-primary mt-1">{summary?.current_points || 0}</p>
            </div>
            <Award className="w-12 h-12 text-primary/50" />
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            1 punto ogni €10 di ordini
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Totale Accumulati</p>
              <p className="text-3xl font-bold text-green-400 mt-1">{summary?.total_earned || 0}</p>
            </div>
            <ArrowUpCircle className="w-10 h-10 text-green-400/50" />
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Punti guadagnati dagli ordini
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Punti Utilizzati</p>
              <p className="text-3xl font-bold text-red-400 mt-1">{summary?.total_spent || 0}</p>
            </div>
            <ArrowDownCircle className="w-10 h-10 text-red-400/50" />
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Punti riscattati per premi
          </p>
        </div>
      </div>

      {/* How to earn points */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Come Guadagnare Punti
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-zinc-800/50 rounded-sm">
            <ShoppingBag className="w-8 h-8 text-blue-400 mb-3" />
            <h4 className="text-white font-medium">Ordini al Ricambista</h4>
            <p className="text-zinc-400 text-sm mt-1">
              1 punto ogni €10 spesi in ordini ricambi
            </p>
          </div>
          <div className="p-4 bg-zinc-800/50 rounded-sm">
            <Gift className="w-8 h-8 text-purple-400 mb-3" />
            <h4 className="text-white font-medium">Campagne Speciali</h4>
            <p className="text-zinc-400 text-sm mt-1">
              Punti bonus su marche sponsorizzate
            </p>
          </div>
          <div className="p-4 bg-zinc-800/50 rounded-sm">
            <Award className="w-8 h-8 text-yellow-400 mb-3" />
            <h4 className="text-white font-medium">Obiettivi Mensili</h4>
            <p className="text-zinc-400 text-sm mt-1">
              Raggiungi i target per bonus extra
            </p>
          </div>
        </div>
      </div>

      {/* Available Rewards */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" />
          Premi Disponibili
        </h3>
        {rewards.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessun premio disponibile al momento</p>
            <p className="text-sm mt-1">Continua ad accumulare punti!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.map((reward) => {
              const canRedeem = (summary?.current_points || 0) >= reward.points_required;
              return (
                <div 
                  key={reward.reward_id} 
                  className={`p-4 border rounded-lg transition-all ${
                    canRedeem 
                      ? "border-primary/50 bg-primary/5 hover:bg-primary/10" 
                      : "border-zinc-700 bg-zinc-800/30 opacity-60"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Star className="w-6 h-6 text-primary" />
                    </div>
                    <span className={`text-lg font-bold ${canRedeem ? "text-primary" : "text-zinc-500"}`}>
                      {reward.points_required} pt
                    </span>
                  </div>
                  <h4 className="text-white font-medium">{reward.name}</h4>
                  <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{reward.description}</p>
                  <Button
                    onClick={() => handleRedeemClick(reward)}
                    disabled={!canRedeem || redeeming === reward.reward_id}
                    className="w-full mt-4"
                    variant={canRedeem ? "default" : "outline"}
                    size="sm"
                  >
                    {redeeming === reward.reward_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : canRedeem ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Riscatta
                      </>
                    ) : (
                      `Mancano ${reward.points_required - (summary?.current_points || 0)} pt`
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transactions History */}
      <div className="card">
        <div className="p-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Storico Movimenti
          </h3>
        </div>
        <div className="divide-y divide-zinc-800">
          {!summary?.transactions?.length ? (
            <div className="p-8 text-center text-zinc-500">
              <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessun movimento ancora</p>
              <p className="text-sm mt-1">I punti verranno accreditati con i tuoi ordini</p>
            </div>
          ) : (
            summary.transactions.map((tx) => (
              <div key={tx.transaction_id} className="p-4 flex items-center justify-between hover:bg-zinc-800/30">
                <div className="flex items-center gap-3">
                  {tx.type === "earned" ? (
                    <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                      <ArrowUpCircle className="w-5 h-5 text-green-400" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-red-500/20 rounded-sm flex items-center justify-center">
                      <ArrowDownCircle className="w-5 h-5 text-red-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-white">{tx.description}</p>
                    <p className="text-xs text-zinc-500">
                      {new Date(tx.created_at).toLocaleString('it-IT')}
                    </p>
                  </div>
                </div>
                <div className={`text-lg font-mono font-bold ${tx.type === "earned" ? "text-green-400" : "text-red-400"}`}>
                  {tx.type === "earned" ? "+" : "-"}{tx.points}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Redeem Confirmation Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Conferma Riscatto</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Stai per riscattare il premio "{selectedReward?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{selectedReward?.name}</p>
                  <p className="text-zinc-400 text-sm">{selectedReward?.description}</p>
                </div>
                <div className="text-2xl font-bold text-primary">
                  -{selectedReward?.points_required} pt
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-sm">
              Dopo il riscatto, il tuo saldo sarà di {(summary?.current_points || 0) - (selectedReward?.points_required || 0)} punti
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRedeemDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleConfirmRedeem} disabled={redeeming}>
              {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma Riscatto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
